import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FileBarChart2, Search, Loader2, Download, Eye, MoreHorizontal,
  Trash2, Send, Home, AlertCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

import {
  getCmaReports, deleteCmaReport, downloadCmaPdf,
} from "../api/prospectingReportsApi";
import { getAgents } from "../../../api/agent";
import CmaReportStarter from "../components/CmaReportStarter";
import { hasAdminAccess } from "../../../utils/roleAccess";
import {
  CMA_REPORT_TYPES, DEFAULT_CMA_REPORT_TYPE, getCmaReportType,
} from "../cmaReportTypes";

const TEAL = "#55c5d0";

const STATUS_STYLES = {
  draft: "bg-[#f2f3f5] text-[#68727d]",
  sent: "bg-[#eef1fd] text-[#4a5bd0]",
  viewed: "bg-[#e9f7ee] text-[#34b56a]",
};

const STATUS_LABELS = { draft: "Draft", sent: "Unread", viewed: "Read" };

const EXPIRY_FILTERS = [
  { key: "", label: "Expiry" },
  { key: "active", label: "Active" },
  { key: "expiring", label: "Expiring soon" },
  { key: "expired", label: "Expired" },
];

const auDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
};

const SALE_LABELS = { private_sale: "Private sale", auction: "Auction", fixed_date: "Fixed-date sale" };
const money = (v) => (v === "" || v == null ? "" : `$${Number(v).toLocaleString("en-AU")}`);

// The columns on Property Data's "My Property Price Statements" list.
const PriceStatementColumns = ({ report }) => {
  const st = report.statement || {};
  const auctionish = st.sale_method === "auction" || st.sale_method === "fixed_date";
  return (
    <>
      <div className="w-24">
        <p className="text-[11px] text-gray-400">Sale</p>
        <p className="text-xs text-gray-700">{SALE_LABELS[st.sale_method] || "-"}</p>
      </div>
      <div className="hidden md:block w-24">
        <p className="text-[11px] text-gray-400">Reserve price</p>
        <p className="text-xs text-gray-700">
          {money(st.reserve_price) || (auctionish ? "Not set" : "Not required")}
        </p>
      </div>
      <div className="hidden md:block w-24">
        <p className="text-[11px] text-gray-400">Sold price</p>
        <p className="text-xs text-gray-700">{money(st.sold_price) || "Not sold yet"}</p>
      </div>
      <div className="w-28">
        <p className="text-[11px] text-gray-400">Published</p>
        <p className={`text-xs ${report.published_at ? "text-gray-700" : "text-amber-600 font-semibold"}`}>
          {report.published_at ? auDate(report.published_at) : "Draft"}
        </p>
      </div>
    </>
  );
};

const CmaListPage = () => {
  const navigate = useNavigate();
  // The chosen tile lives in the URL so Back from the wizard lands on it again.
  const [searchParams, setSearchParams] = useSearchParams();
  const reportType = getCmaReportType(searchParams.get("type") || DEFAULT_CMA_REPORT_TYPE);
  const { user } = useSelector((state) => state.auth);
  const isOfficeWide = hasAdminAccess(user) || (user?.role || "").toLowerCase() === "pa";

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [expiryFilter, setExpiryFilter] = useState("");
  const [agents, setAgents] = useState([]);
  const [menuFor, setMenuFor] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: reportType.key };
      if (search.trim()) params.q = search.trim();
      if (agentFilter) params.agent = agentFilter;
      if (expiryFilter) params.expiry = expiryFilter;
      const { data } = await getCmaReports(params);
      setReports(data?.results || []);
    } catch (error) {
      toast.error("Could not load your CMA reports.");
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [search, agentFilter, expiryFilter, reportType.key]);

  useEffect(() => {
    // Debounced so typing in the search box does not fire a request per keystroke.
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    if (!isOfficeWide) return;
    getAgents()
      .then(({ data }) => setAgents(Array.isArray(data) ? data : data?.results || []))
      .catch(() => setAgents([]));
  }, [isOfficeWide]);

  const handleDelete = async (report) => {
    if (!window.confirm(`Delete the CMA for ${report.full_address}? This cannot be undone.`)) return;
    try {
      await deleteCmaReport(report.id);
      toast.success("Report deleted.");
      setReports((prev) => prev.filter((r) => r.id !== report.id));
    } catch (error) {
      toast.error("Could not delete the report.");
    }
    setMenuFor(null);
  };

  const handlePdf = async (report) => {
    setDownloading(report.id);
    try {
      const { data } = await downloadCmaPdf(report.id);
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${report.report_type_label || "CMA Report"}, ${report.full_address}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // The API returns 503 when neither PDF engine is available on the server.
      toast.error("Could not build the PDF. The digital report link still works.");
    } finally {
      setDownloading(null);
    }
  };

  const openDigital = (report) => {
    window.open(`/cma/${report.share_token}`, "_blank", "noopener");
  };

  const emptyMessage = useMemo(() => {
    if (search || agentFilter || expiryFilter) return "No reports match these filters.";
    return `No ${reportType.label}s yet. Enter a target address above to start one.`;
  }, [search, agentFilter, expiryFilter, reportType.label]);

  const chooseType = (key) => {
    setSearchParams(key === DEFAULT_CMA_REPORT_TYPE ? {} : { type: key }, { replace: true });
  };


  return (
    <div className="p-4 md:p-6">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileBarChart2 className="w-6 h-6" style={{ color: TEAL }} />
          CMA Reports
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Pick a report, enter the target address and search options, then pick the records to include.
        </p>
      </div>

      {/* The three report tiles, as on Property Data. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" role="tablist" aria-label="Report type">
        {CMA_REPORT_TYPES.map((type) => {
          const Icon = type.icon;
          const active = type.key === reportType.key;
          return (
            <button
              key={type.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => chooseType(type.key)}
              className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-colors ${
                active ? "bg-[#55c5d0]/10" : "bg-white border-gray-100 hover:border-gray-200"
              }`}
              style={active ? { borderColor: TEAL } : undefined}
            >
              <span
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: active ? TEAL : "#f2f3f5" }}
              >
                <Icon className="w-5 h-5" style={{ color: active ? "#fff" : "#68727d" }} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-bold text-gray-900">{type.label}</span>
                <span className="block text-xs text-gray-500 mt-0.5">{type.blurb}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Target address + Search Options, same layout as Property Data. */}
      <div className="mb-6">
        <CmaReportStarter
          key={reportType.key}
          reportType={reportType.key}
          onCreated={(report) => navigate(`/prospecting/cma/${report.id}`)}
        />
      </div>

      <h2 className="text-sm font-bold text-gray-900 mb-3">Your {reportType.label}s</h2>

      {/* Filters, the same trio as Ignite: search, lead agent, expiry. */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by address or vendor"
            className="pl-9 pr-3 py-2 w-64 rounded-full border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#55c5d0]/40"
          />
        </div>

        {isOfficeWide && (
          <select
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className="px-3 py-2 rounded-full border border-gray-200 text-sm bg-white"
          >
            <option value="">Lead Agents</option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>{agent.name || agent.email}</option>
            ))}
          </select>
        )}

        <select
          value={expiryFilter}
          onChange={(e) => setExpiryFilter(e.target.value)}
          className="px-3 py-2 rounded-full border border-gray-200 text-sm bg-white"
        >
          {EXPIRY_FILTERS.map((f) => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : reports.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded-xl py-16 text-center">
          <FileBarChart2 className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">{emptyMessage}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {reports.map((report) => (
            <div
              key={report.id}
              className="flex flex-wrap md:flex-nowrap items-center gap-4 p-3 bg-white border border-gray-100 rounded-xl hover:border-gray-200 transition-colors"
            >
              <div className="w-16 h-12 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                {report.image_url ? (
                  <img src={report.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Home className="w-5 h-5 text-gray-300" />
                )}
              </div>

              <button
                type="button"
                onClick={() => navigate(`/prospecting/cma/${report.id}`)}
                className="flex-1 min-w-[180px] text-left"
              >
                <p className="text-sm font-semibold text-gray-900 truncate">{report.full_address}</p>
                <p className="text-xs text-gray-500">
                  {[report.suburb, report.state, report.postcode].filter(Boolean).join(" ")}
                </p>
              </button>

              <div className="hidden lg:block w-32">
                <p className="text-[11px] text-gray-400">Lead Agent</p>
                <p className="text-xs text-gray-700 truncate">{report.agent?.name || "-"}</p>
              </div>

              {reportType.statement ? (
                <PriceStatementColumns report={report} />
              ) : (
                <>
                <div className="w-24">
                  <p className="text-[11px] text-gray-400">Status</p>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_STYLES[report.status] || ""}`}>
                    {STATUS_LABELS[report.status] || report.status}
                  </span>
                </div>

                <div className="hidden md:block w-20">
                  <p className="text-[11px] text-gray-400">Views</p>
                  <p className="text-xs text-gray-700">{report.view_count || "-"}</p>
                </div>

                <div className="w-28">
                  <p className="text-[11px] text-gray-400">Expiry</p>
                  <p className={`text-xs ${report.is_expired ? "text-red-600 font-semibold" : "text-gray-700"}`}>
                    {report.is_expired
                      ? "Expired"
                      : report.days_to_expiry != null
                        ? `${report.days_to_expiry} days`
                        : auDate(report.expires_at) || "-"}
                  </p>
                </div>
                </>
              )}

              <div className="flex items-center gap-1 ml-auto relative">
                <button
                  type="button"
                  onClick={() => (reportType.statement && !report.published_at
                    ? navigate(`/prospecting/cma/${report.id}`)
                    : openDigital(report))}
                  className="px-3 py-1.5 text-xs font-semibold text-gray-700 hover:text-gray-900"
                >
                  {reportType.statement && !report.published_at ? "Finish" : "View Report"}
                </button>
                <button
                  type="button"
                  onClick={() => handlePdf(report)}
                  disabled={downloading === report.id}
                  className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-semibold text-gray-700 hover:text-gray-900 disabled:opacity-50"
                >
                  {downloading === report.id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Download className="w-3.5 h-3.5" />}
                  PDF
                </button>
                <button
                  type="button"
                  onClick={() => setMenuFor(menuFor === report.id ? null : report.id)}
                  className="p-1.5 rounded-lg hover:bg-gray-50 text-gray-400"
                  aria-label="More actions"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>

                {menuFor === report.id && (
                  <div className="absolute right-0 top-9 z-20 w-44 bg-white border border-gray-100 rounded-lg shadow-lg py-1">
                    <button
                      type="button"
                      onClick={() => navigate(`/prospecting/cma/${report.id}`)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      <Eye className="w-3.5 h-3.5" /> Edit report
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/prospecting/cma/${report.id}?step=4`)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      <Send className="w-3.5 h-3.5" /> Send to vendor
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(report)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg p-3">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" />
        <p>
          Records come from Collings listings and the Homer market feed. Property Data covers every
          Victorian sale and we do not, so use <strong>Add Property</strong> on Select Records for any
          sale that is missing.
        </p>
      </div>

    </div>
  );
};

export default CmaListPage;
